// 📄 pages/Proyectos/TablasCatalogo.tsx
// Consulta (solo lectura) del Catálogo de Tablas SAP dentro de un proyecto
// — vive en /proyecto/:projectId/tablas, dentro del grupo "Herramientas"
// del sidebar. El catálogo ESTÁNDAR es global (no por proyecto, ver
// catalogoTablasService.ts): el CRUD completo vive en /admin > Catálogo de
// Tablas; acá cualquier consultor solo lo consulta. Las Tablas Z (propias
// de este proyecto, ver tablaZService.ts) se administran en Configuración
// > Desarrollo Z y se muestran acá aparte, solo lectura. Mismo patrón
// exacto que BadiCatalogo.tsx.
import { useEffect, useState } from 'react';
import { RefreshCw, Table2, Info } from 'lucide-react';
import { TablaEstandar, getTablasEstandar } from '../../services/catalogoTablasService.ts';
import { TablaZ, getTablasZ } from '../../services/tablaZService.ts';
import TablaCatalogGrid from '../../components/tablas/TablaCatalogGrid.tsx';
import { useProject } from '../../context/ProjectContext.tsx';

type VistaTablas = 'todas' | 'estandar' | 'z';

export default function TablasCatalogo() {
  const { projectId } = useProject();
  const [vista, setVista] = useState<VistaTablas>('todas');
  const [tablas, setTablas] = useState<TablaEstandar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tablasZ, setTablasZ] = useState<TablaZ[]>([]);
  const [loadingZ, setLoadingZ] = useState(true);
  const [errorZ, setErrorZ] = useState<string | null>(null);

  useEffect(() => {
    getTablasEstandar()
      .then(setTablas)
      .catch((err) => { console.error('Error cargando el catálogo de Tablas:', err); setError('No se pudo cargar el catálogo de Tablas.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!projectId) { setLoadingZ(false); return; }
    setLoadingZ(true);
    getTablasZ(projectId)
      .then(setTablasZ)
      .catch((err) => { console.error('Error cargando las Tablas Z del proyecto:', err); setErrorZ('No se pudieron cargar las Tablas Z de este proyecto.'); })
      .finally(() => setLoadingZ(false));
  }, [projectId]);

  const zCount = tablasZ.length;

  const OPCIONES_VISTA: { id: VistaTablas; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'estandar', label: 'Estándar' },
    { id: 'z', label: zCount > 0 ? `Tablas Z (${zCount})` : 'Tablas Z' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPCIONES_VISTA.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              vista === id
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {vista !== 'z' && (
        <>
          <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración.</span>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {loading ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando catálogo...</p>
            </div>
          ) : tablas.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Table2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Todavía no hay tablas registradas en el catálogo. Un Admin puede cargarlas desde /admin.</p>
            </div>
          ) : (
            <TablaCatalogGrid tablas={tablas} readOnly />
          )}
        </>
      )}

      {vista !== 'estandar' && projectId && (
        <div className={vista === 'todas' ? 'pt-6 border-t border-white/10 space-y-4' : 'space-y-4'}>
          <div>
            <h3 className="text-sm font-bold text-white">Tablas Z de este proyecto</h3>
            <p className="text-xs text-gray-500 mt-0.5">Tablas SAP personalizadas configuradas para este proyecto. Solo tú y tu equipo las ven.</p>
          </div>

          {errorZ && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorZ}</div>}

          {loadingZ ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando Tablas Z...</p>
            </div>
          ) : tablasZ.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Table2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Aún no hay Tablas Z en este proyecto. Agrégalas en Configuración &gt; Desarrollo Z.</p>
            </div>
          ) : (
            <TablaCatalogGrid tablas={tablasZ} esZ readOnly />
          )}
        </div>
      )}
    </div>
  );
}
